import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, Search, Loader2, CalendarClock, AlertTriangle, Check,
} from 'lucide-react';
import { getFixturesByDay, getClientDays, FixturesDay, DayFixture } from '../services/api';

/* ---------- dates ---------------------------------------------------------
   All date maths runs on `YYYY-MM-DD` strings via a midday local Date, so a
   day never slips either side of a DST boundary or a UTC conversion.        */

const toKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fromKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};

const addDays = (key: string, days: number) => {
  const d = fromKey(key);
  d.setDate(d.getDate() + days);
  return toKey(d);
};

const todayKey = () => toKey(new Date());

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const STRIP_DAYS = 14;

/** "Friday 14 August 2026" — says in words which day is on screen. */
function longDate(key: string) {
  const d = fromKey(key);
  return `${DAY_NAMES_FULL[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

/* ---------- match status --------------------------------------------------
   Opta's F1 feed reports progress in the MatchInfo `Period` attribute. These are
   the values seen in the imported data plus the obvious siblings; anything else
   falls through to the raw value rather than being silently hidden.          */

type Presentation = { kind: 'time' | 'score' | 'flag'; label: string; live?: boolean };

function presentation(f: DayFixture): Presentation {
  const status = (f.matchStatus || '').toLowerCase();

  if (status === 'postponed') return { kind: 'flag', label: 'Match Postponed' };
  if (status === 'abandoned') return { kind: 'flag', label: 'Match Abandoned' };
  if (status === 'cancelled' || status === 'canceled') return { kind: 'flag', label: 'Match Cancelled' };
  if (status === 'suspended') return { kind: 'flag', label: 'Match Suspended' };

  if (status === 'fulltime' || status === 'ft') return { kind: 'score', label: 'FT' };
  if (status === 'halftime') return { kind: 'score', label: 'HT', live: true };
  if (status === 'firsthalf') return { kind: 'score', label: '1st Half', live: true };
  if (status === 'secondhalf') return { kind: 'score', label: '2nd Half', live: true };
  if (status.startsWith('extra')) return { kind: 'score', label: 'Extra Time', live: true };
  if (status === 'shootout' || status === 'penaltyshootout') return { kind: 'score', label: 'Penalties', live: true };
  if (status === 'prematch' || status === '') return { kind: 'time', label: '' };

  // Unrecognised period: if there is a score, show it; otherwise fall back to kick-off.
  if (f.homeScore != null && f.awayScore != null) return { kind: 'score', label: f.matchStatus };
  return { kind: 'time', label: '' };
}

const shortTime = (t: string | null) => (t ? t.substring(0, 5) : '');

/** "Millwall win 2-0 on pens" — only when a shoot-out actually decided it. */
function penaltyLine(f: DayFixture): string | null {
  const { homePenaltyScore: h, awayPenaltyScore: a } = f;
  if (h == null || a == null || h === a) return null;
  const winner = h > a ? f.homeTeamName : f.awayTeamName;
  const hi = Math.max(h, a);
  const lo = Math.min(h, a);
  return `${winner || 'Winner'} win ${hi}-${lo} on pens`;
}

/* ---------- client indicator ---------------------------------------------- */

/**
 * The tick, now used only on the date strip.
 *
 * It used to sit on the team names too, overlaid on the end of the word. That is gone:
 * a marker floating over a name is a thing to decode, whereas type doing the work is
 * simply read. See `TeamName`.
 *
 * Split so the strip can swap only the fill. Both halves must stay separate: two
 * competing `bg-` classes on one element are decided by stylesheet order, not by which
 * one you wrote last, so building the string from a base avoids a coin toss.
 */
const TICK_BASE =
  'inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-white transition-colors';
const TICK_CLASS = `${TICK_BASE} bg-uz-purple/30`;

/** Bold, with a UZ Purple rule beneath — how a client team is marked on a fixture row. */
const CLIENT_TEAM_CLASS =
  'font-bold underline decoration-uz-purple decoration-2 underline-offset-4';

/**
 * A team name, marked when the club is one of ours.
 *
 * Bold with a purple rule under it rather than a badge beside it. The rule sits below the
 * baseline and the browser breaks it around descenders, so it never crosses a letter — and
 * because it is the type itself carrying the meaning, a client team reads as emphasised
 * rather than annotated.
 *
 * Not a link. It used to open the client record in Central System, since Events has no
 * client page of its own, but the mark is now the type itself — and type that changes
 * colour and navigates on click is a link pretending to be emphasis. The client name is
 * still there on hover, which is the part that was actually worth keeping: the club and
 * the client record are not always named the same thing.
 *
 * Marked once however many client records the club holds. Everton has a GC1 and a GC2,
 * both live, and marking it twice read as a rendering fault. Names are de-duplicated for
 * the tooltip too, since those records usually share one.
 */
function TeamName({ name, clients, align }: {
  name: string;
  clients: { id: number; name: string }[];
  align: 'left' | 'right';
}) {
  const alignment = align === 'right' ? 'text-right' : 'text-left';
  const base = `block text-base text-uz-black truncate ${alignment}`;
  if (clients.length === 0) return <span className={base}>{name}</span>;

  const names = Array.from(new Set(clients.map(c => c.name)));
  const label = names.length > 1 ? `Our clients: ${names.join(', ')}` : `Our client: ${names[0]}`;
  return (
    <span className={`${base} ${CLIENT_TEAM_CLASS}`} title={label}>
      {name}
    </span>
  );
}

/* ---------- fixture row ---------------------------------------------------- */

function FixtureRow({ f }: { f: DayFixture }) {
  const p = presentation(f);
  const pens = p.kind === 'score' ? penaltyLine(f) : null;
  const home = f.homeTeamName || f.homeTeamId;
  const away = f.awayTeamName || f.awayTeamId;

  return (
    <div
      className={`border-l-4 px-4 py-3.5 transition-colors ${
        f.isClientFixture
          ? 'border-uz-purple bg-white hover:bg-uz-wash/60'
          : 'border-transparent bg-white hover:bg-uz-cool'
      }`}
    >
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-5">
        <div className="flex items-center justify-end min-w-0">
          <TeamName name={home} clients={f.homeClients} align="right" />
        </div>

        <div className="text-center shrink-0 min-w-[5rem]">
          {p.kind === 'time' && (
            <span className="text-lg text-uz-muted tabular-nums">
              {shortTime(f.matchTime) || 'TBC'}
            </span>
          )}
          {p.kind === 'score' && (
            <span className="text-xl font-extrabold text-uz-black tabular-nums">
              {f.homeScore ?? '-'}
              <span className="font-normal text-uz-warm mx-1.5">–</span>
              {f.awayScore ?? '-'}
            </span>
          )}
          {p.kind === 'flag' && (
            <span className="text-lg font-bold text-uz-warm">P – P</span>
          )}
        </div>

        <div className="flex items-center justify-start min-w-0">
          <TeamName name={away} clients={f.awayClients} align="left" />
        </div>
      </div>

      {(p.label || pens) && (
        <div className="mt-1.5 text-center">
          {p.label && (
            <span
              className={`inline-flex items-center gap-1.5 text-sm ${
                p.live ? 'font-bold text-uz-purple' : 'text-uz-muted'
              }`}
            >
              {p.live && (
                <span className="w-1.5 h-1.5 rounded-full bg-uz-purple animate-pulse" aria-hidden="true" />
              )}
              {p.label}
            </span>
          )}
          {pens && <div className="text-sm text-uz-muted mt-0.5">{pens}</div>}
        </div>
      )}
    </div>
  );
}

/* ---------- shared surfaces ------------------------------------------------
   The brand guide has nothing to say about cards, radii or buttons, so these
   are house style: one radius, one hairline border, no drop shadows away from
   the sign-in card.                                                          */

const CARD = 'bg-white rounded-2xl border border-uz-edge/60';
const BUTTON_PRIMARY =
  'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-uz-purple text-white font-bold ' +
  'text-base hover:bg-uz-deep transition-colors';

/* ---------- page ----------------------------------------------------------- */

export default function Fixtures() {
  const [date, setDate] = useState<string>(todayKey());
  const [scope, setScope] = useState<'clients' | 'all'>('clients');
  const [search, setSearch] = useState('');
  const [day, setDay] = useState<FixturesDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Client-fixture counts for the days currently in the strip, so each one can say
  // whether it is worth opening. Sparse — a date with none is simply absent.
  const [clientDays, setClientDays] = useState<Record<string, number>>({});

  // First day shown in the date strip. Opens three days back so the most recent
  // results and the coming fortnight's fixtures are both one click away.
  const [windowStart, setWindowStart] = useState<string>(() => addDays(todayKey(), -3));

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getFixturesByDay(date, scope)
      .then(setDay)
      .catch((err: any) => {
        setError(err?.message || 'Could not load fixtures.');
        setDay(null);
      })
      .finally(() => setLoading(false));
  }, [date, scope]);

  useEffect(() => { load(); }, [load]);

  // Collapsing is per competition and deliberately resets when the day changes — a
  // section you shut on Saturday has no bearing on what you want to see on Sunday.
  useEffect(() => { setCollapsed(new Set()); }, [date, scope]);

  const toggleCollapsed = (id: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  // Jumping to a date from outside the strip (the picker, or Today) has to bring the
  // strip with it. Picking a day *inside* the strip must not move it — otherwise
  // paging back two weeks would drag the window straight back to the selected day.
  const jumpTo = (key: string) => {
    setDate(key);
    setWindowStart(addDays(key, -3));
  };

  const stripDays = useMemo(
    () => Array.from({ length: STRIP_DAYS }, (_, i) => addDays(windowStart, i)),
    [windowStart],
  );

  // Markers for the visible fortnight, refetched when the window moves. Deliberately
  // silent on failure: these are a hint, and a strip with no ticks is still a working
  // strip — surfacing an error here would be louder than the feature is worth.
  useEffect(() => {
    let cancelled = false;
    getClientDays(stripDays[0], stripDays[stripDays.length - 1])
      .then(r => { if (!cancelled) setClientDays(r.days); })
      .catch(() => { if (!cancelled) setClientDays({}); });
    return () => { cancelled = true; };
  }, [stripDays]);

  const monthLabel = useMemo(() => {
    const first = fromKey(stripDays[0]);
    const last = fromKey(stripDays[stripDays.length - 1]);
    const firstLabel = `${MONTH_NAMES[first.getMonth()]} ${first.getFullYear()}`;
    const lastLabel = `${MONTH_NAMES[last.getMonth()]} ${last.getFullYear()}`;
    return firstLabel === lastLabel ? firstLabel : `${MONTH_NAMES[first.getMonth()]} – ${lastLabel}`;
  }, [stripDays]);

  // The search box filters the day already loaded, and stays applied as you move
  // between days — so you can type a team once and step forward to find their game.
  const filtered = useMemo(() => {
    if (!day) return [];
    const q = search.trim().toLowerCase();
    if (!q) return day.competitions;
    return day.competitions
      .map(comp => {
        if (comp.name.toLowerCase().includes(q)) return comp;
        const fixtures = comp.fixtures.filter(f =>
          (f.homeTeamName || '').toLowerCase().includes(q)
          || (f.awayTeamName || '').toLowerCase().includes(q)
          || f.homeClients.some(c => c.name.toLowerCase().includes(q))
          || f.awayClients.some(c => c.name.toLowerCase().includes(q)));
        return { ...comp, fixtures };
      })
      .filter(comp => comp.fixtures.length > 0);
  }, [day, search]);

  const shownCount = filtered.reduce((n, c) => n + c.fixtures.length, 0);
  const today = todayKey();

  return (
    <div className="space-y-5">
      {/* Title */}
      <div className="flex items-start gap-3.5">
        <span className="shrink-0 w-11 h-11 rounded-xl bg-uz-wash flex items-center justify-center">
          <CalendarClock className="w-6 h-6 text-uz-purple" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl">Football Fixtures &amp; Results</h1>
          <p className="text-base text-uz-muted mt-0.5">{longDate(date)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className={`${CARD} p-4 sm:p-5 space-y-5`}>
        {/* Search */}
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-uz-warm" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Enter a team or competition"
            aria-label="Filter by team or competition"
            className="w-full pl-11 pr-3.5 py-3 bg-uz-cool border border-uz-edge/60 rounded-xl text-base
                       text-uz-black placeholder:text-uz-muted focus:bg-white focus:border-uz-purple
                       focus:outline-none focus:ring-1 focus:ring-uz-purple transition-colors"
          />
        </div>

        {/* Month + jump-to-date */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg">{monthLabel}</h2>
          <div className="flex items-center gap-2">
            {date !== today && (
              <button
                onClick={() => jumpTo(today)}
                className="px-4 py-2 rounded-lg text-sm font-bold text-uz-purple bg-uz-wash
                           hover:bg-uz-purple hover:text-white transition-colors"
              >
                Today
              </button>
            )}
            <input
              type="date"
              value={date}
              onChange={e => e.target.value && jumpTo(e.target.value)}
              aria-label="Jump to date"
              className="px-3 py-2 border border-uz-edge/60 rounded-lg text-sm text-uz-black
                         focus:outline-none focus:border-uz-purple focus:ring-1 focus:ring-uz-purple"
            />
          </div>
        </div>

        {/* Date rail. Chips rather than the underlined tabs this replaced — the selected
            day now holds a solid purple fill, which survives being glanced at. */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWindowStart(addDays(windowStart, -7))}
            aria-label="Earlier dates"
            className="shrink-0 w-9 h-16 flex items-center justify-center rounded-lg text-uz-muted
                       hover:text-uz-purple hover:bg-uz-wash transition-colors"
          >
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>

          <div className="flex-1 flex gap-1 overflow-x-auto no-scrollbar">
            {stripDays.map(key => {
              const d = fromKey(key);
              const selected = key === date;
              const isToday = key === today;
              const clientCount = clientDays[key] || 0;
              const clientLabel = clientCount === 1
                ? '1 fixture for our clients'
                : `${clientCount} fixtures for our clients`;
              return (
                <button
                  key={key}
                  onClick={() => setDate(key)}
                  aria-current={selected ? 'date' : undefined}
                  aria-label={clientCount ? `${longDate(key)} — ${clientLabel}` : longDate(key)}
                  title={clientCount ? clientLabel : undefined}
                  className={`flex-1 shrink-0 min-w-[3.25rem] h-16 rounded-xl flex flex-col items-center
                              justify-center transition-colors ${
                    selected
                      ? 'bg-uz-purple text-white'
                      : isToday
                        ? 'text-uz-purple ring-1 ring-uz-edge hover:bg-uz-wash'
                        : 'text-uz-black hover:bg-uz-wash'
                  }`}
                >
                  <span className={`block text-sm leading-tight ${selected || isToday ? 'font-bold' : ''}`}>
                    {isToday ? 'Today' : DAY_NAMES[d.getDay()]}
                  </span>
                  <span className={`block text-lg leading-tight tabular-nums ${selected ? 'font-extrabold' : 'font-bold'}`}>
                    {d.getDate()}
                  </span>
                  {/* Sits under the number rather than in a corner: at this width a corner
                      badge collides with "Today". The space is reserved whether or not
                      there is a tick, so the numbers stay on one line as you page.

                      Same 30% as the tick on a fixture row. On the selected day the fill
                      switches to white, because purple at 30% over a purple chip is the
                      chip — the circle would disappear and leave the check floating. */}
                  <span className="h-[18px] mt-0.5 flex items-center" aria-hidden="true">
                    {clientCount > 0 && (
                      <span className={`${TICK_BASE} ${selected ? 'bg-white/30' : 'bg-uz-purple/30'}`}>
                        <Check className="w-2.5 h-2.5" strokeWidth={4} />
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setWindowStart(addDays(windowStart, 7))}
            aria-label="Later dates"
            className="shrink-0 w-9 h-16 flex items-center justify-center rounded-lg text-uz-muted
                       hover:text-uz-purple hover:bg-uz-wash transition-colors"
          >
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Scope toggle + legend */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-uz-edge/50">
          <div className="inline-flex rounded-xl bg-uz-cool border border-uz-edge/60 p-1" role="group">
            {(['clients', 'all'] as const).map(value => (
              <button
                key={value}
                onClick={() => setScope(value)}
                aria-pressed={scope === value}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  scope === value
                    ? 'bg-uz-purple text-white font-bold'
                    : 'text-uz-black hover:text-uz-purple'
                }`}
              >
                {value === 'clients' ? 'Our clients' : 'All fixtures'}
                {day && (
                  <span className={scope === value ? 'text-white/70' : 'text-uz-muted'}>
                    {' '}({value === 'clients' ? day.clientFixtureCount : day.totalFixtureCount})
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            {search.trim() && !loading && (
              <span className="text-sm text-uz-muted">
                {shownCount} matching {shownCount === 1 ? 'fixture' : 'fixtures'}
              </span>
            )}
            {/* A noun phrase, not a sentence. The tick marks a date that holds one or more
                client fixtures, and any wording with a verb has to choose a number —
                "client(s) is playing" mismatches, "are playing" reads oddly for a single
                club. No verb, no agreement to get wrong.

                Only the tick is explained. The underline on a team name reads as emphasis
                without a key, and spelling out both made this row busier than what it was
                clarifying. */}
            <span className="flex items-center gap-2 text-sm text-uz-muted">
              <span className={TICK_CLASS}>
                <Check className="w-2.5 h-2.5" strokeWidth={4} aria-hidden="true" />
              </span>
              Client fixtures
            </span>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading && (
        <div className="flex items-center justify-center gap-2.5 py-20 text-uz-muted">
          <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
          Loading fixtures…
        </div>
      )}

      {!loading && error && (
        <div className={`${CARD} p-5 flex items-start gap-3.5`}>
          <AlertTriangle className="w-5 h-5 text-uz-purple shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-base text-uz-black">{error}</p>
            <button onClick={load} className={`${BUTTON_PRIMARY} mt-3.5`}>Try again</button>
          </div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className={`${CARD} p-12 text-center`}>
          <p className="text-base text-uz-muted">
            {search.trim()
              ? `Nothing matching “${search.trim()}” on this date.`
              : scope === 'clients' && (day?.totalFixtureCount ?? 0) > 0
                ? 'None of our clients are playing on this date.'
                : 'No fixtures on this date.'}
          </p>
          {!search.trim() && scope === 'clients' && (day?.totalFixtureCount ?? 0) > 0 && (
            <button onClick={() => setScope('all')} className={`${BUTTON_PRIMARY} mt-5`}>
              Show all {day!.totalFixtureCount} fixtures
            </button>
          )}
        </div>
      )}

      {/* Competitions stay in the order the API sent them. That order is the `displayOrder`
          column, maintained in Central System and read-only here — sorting clients to the
          top locally would quietly disagree with the other app. The scope toggle is what
          narrows this to clients. */}
      {!loading && !error && filtered.map(comp => {
        const isCollapsed = collapsed.has(comp.providerCompetitionId);
        const clientCount = comp.fixtures.filter(f => f.isClientFixture).length;
        return (
          <section key={comp.providerCompetitionId} className={CARD}>
            <h2>
              <button
                onClick={() => toggleCollapsed(comp.providerCompetitionId)}
                aria-expanded={!isCollapsed}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left rounded-2xl
                           hover:bg-uz-wash/50 transition-colors"
              >
                <ChevronDown
                  className={`w-5 h-5 shrink-0 text-uz-purple transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-lg font-extrabold text-uz-black truncate">{comp.name}</span>
                  <span className="block text-sm font-normal text-uz-muted">
                    {comp.typeName ? `${comp.typeName} · ` : ''}
                    {comp.fixtures.length} {comp.fixtures.length === 1 ? 'fixture' : 'fixtures'}
                    {clientCount > 0 && ` · ${clientCount} ours`}
                  </span>
                </span>
              </button>
            </h2>
            {!isCollapsed && (
              <div className="divide-y divide-uz-edge/40 border-t border-uz-edge/50">
                {comp.fixtures.map(f => <FixtureRow key={f.id} f={f} />)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
